import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, Search, Receipt } from 'lucide-react';
import { primaryButton, ghostButton, inputStyle, glass, glassLight, T, StatCard, EmptyState, Field } from '../ui';
interface Cliente {
  id: string;
  nombre: string;
  codigo?: string;
}
interface Venta {
  id: string;
  numeroFactura: string;
  clienteId: string;
  clienteNombre: string;
  fecha: string;
  cantidad: number;
  precio: number;
  total: number;
  ruta: string;
  supervisor: string;
  observaciones: string;
}
interface VentasProps {
  ventas: Venta[];
  clientes: Cliente[];
  onAddVenta: (venta: Venta) => void;
  onDeleteVenta: (id: string) => void;
  onUpdateVenta: (venta: Venta) => void;
}
const iconBtn: React.CSSProperties = {
  width: 30,
  height: 30,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: T.surface,
  cursor: 'pointer'
};
function fmtRD(n: number) {
  return new Intl.NumberFormat('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(n);
}
export function Ventas({
  ventas,
  clientes,
  onAddVenta,
  onDeleteVenta,
  onUpdateVenta
}: VentasProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFecha, setFilterFecha] = useState('');
  const [formData, setFormData] = useState<Partial<Venta>>({
    cantidad: 0,
    precio: 0,
    total: 0
  });
  const [selectedCliente, setSelectedCliente] = useState<string>('');
  const handleCalculateTotal = (cantidad: number, precio: number) => {
    const total = cantidad * precio;
    setFormData(prev => ({
      ...prev,
      cantidad,
      precio,
      total
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.numeroFactura || !selectedCliente || !formData.fecha || !formData.cantidad || !formData.precio) {
      alert('Por favor completa los campos obligatorios');
      return;
    }
    const cliente = clientes.find(c => c.id === selectedCliente);
    if (editingId) {
      onUpdateVenta({
        ...formData,
        id: editingId,
        clienteId: selectedCliente,
        clienteNombre: cliente?.nombre || ''
      } as Venta);
      setEditingId(null);
    } else {
      onAddVenta({
        ...formData,
        id: Date.now().toString(),
        clienteId: selectedCliente,
        clienteNombre: cliente?.nombre || ''
      } as Venta);
    }
    setFormData({
      cantidad: 0,
      precio: 0,
      total: 0
    });
    setSelectedCliente('');
    setShowForm(false);
  };
  const handleEdit = (venta: Venta) => {
    setFormData(venta);
    setSelectedCliente(venta.clienteId);
    setEditingId(venta.id);
    setShowForm(true);
  };
  const filteredVentas = ventas.filter(v => {
    const matchSearch = v.numeroFactura.toLowerCase().includes(searchTerm.toLowerCase()) || v.clienteNombre.toLowerCase().includes(searchTerm.toLowerCase());
    const matchFecha = !filterFecha || v.fecha === filterFecha;
    return matchSearch && matchFecha;
  });
  const ventasHoy = ventas.filter(v => v.fecha === new Date().toISOString().split('T')[0]).length;
  const totalVentas = ventas.reduce((sum, v) => sum + v.total, 0);
  const totalCantidad = ventas.reduce((sum, v) => sum + v.cantidad, 0);
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  }} data-id="element-1198" data-mp="2phfizn9pbpsb">
      {/* Header Stats */}
      <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: 14
    }} data-id="element-1199" data-mp="2geehjzvs6muz">
        
        <StatCard label="Total ventas" value={String(ventas.length)} note="Facturas registradas" accent={T.info} data-id="element-1200" data-mp="2leai1p59rw5s" />
        
        <StatCard label="Hoy" value={String(ventasHoy)} note="Facturadas en el día" accent={T.ok} data-id="element-1201" data-mp="ent4qbo7bukg" />
        
        <StatCard label="Monto total" value={`RD$ ${fmtRD(totalVentas)}`} note="Acumulado histórico" accent={T.warn} data-id="element-1202" data-mp="jk1qw90tgo14" />
        
        <StatCard label="Cantidad total" value={`${totalCantidad.toFixed(0)} gal`} note="Volumen distribuido" accent="#8B5CF6" data-id="element-1203" data-mp="2aqfuug9sl6fp" />
        
      </div>

      {/* Controles */}
      <div style={{
      ...glassLight,
      padding: 16
    }} data-id="element-1204" data-mp="25sxtpc20nvi9">
        <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        alignItems: 'center'
      }} data-id="element-1205" data-mp="21oyfj047l8s2">
          
          <div style={{
          flex: '1 1 240px',
          position: 'relative'
        }} data-id="element-1206" data-mp="1rzomni1jjm2w">
            <Search size={16} style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} data-id="element-1207" data-mp="1n0lgtvdqlbtv" />
            
            <input type="text" placeholder="Buscar por factura o cliente..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{
            ...inputStyle,
            paddingLeft: 36
          }} data-id="element-1208" data-mp="1e0f80mszh011" />
            
          </div>
          <input type="date" value={filterFecha} onChange={e => setFilterFecha(e.target.value)} style={{
          ...inputStyle,
          width: 'auto'
        }} data-id="element-1209" data-mp="3knwdfp5maek1" />
          
          <button onClick={() => {
          setShowForm(!showForm);
          setEditingId(null);
          setFormData({
            cantidad: 0,
            precio: 0,
            total: 0
          });
          setSelectedCliente('');
        }} style={{
          ...primaryButton,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6
        }} data-id="element-1210" data-mp="3tq5m09hbrxus">
            
            <Plus size={15} data-id="element-1211" data-mp="3axbeg0m1780l" /> Registrar Venta
          </button>
        </div>
      </div>

      {/* Formulario */}
      <AnimatePresence data-id="element-1212" data-mp="3fqnqzcdfrocz">
        {showForm && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} style={{
        overflow: 'hidden'
      }} data-id="element-1213" data-mp="2i9p3g7ah513p">
          
            <div style={{
          ...glass,
          padding: 20
        }} data-id="element-1214" data-mp="3lkmdk34jwwxv">
              <h3 style={{
            fontSize: 15,
            fontWeight: 800,
            marginBottom: 16,
            color: T.text
          }} data-id="element-1215" data-mp="3liicru1y8e1t">
              
                {editingId ? 'Editar Venta' : 'Registrar Nueva Venta'}
              </h3>
              <form onSubmit={handleSubmit} data-id="element-1216" data-mp="37lv1snd5vncw">
                <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 14
            }} data-id="element-1217" data-mp="37modm2qz931w">
                
                  <Field label="Número de factura *" data-id="element-1218" data-mp="3gmb43ti64og6">
                    <input type="text" value={formData.numeroFactura || ''} onChange={e => setFormData({
                  ...formData,
                  numeroFactura: e.target.value
                })} style={inputStyle} placeholder="FAC-2024-0001" data-id="element-1219" data-mp="3bmosfohzng9h" />
                  
                  </Field>
                  <Field label="Fecha *" data-id="element-1220" data-mp="zdgi4w9rydof">
                    <input type="date" value={formData.fecha || ''} onChange={e => setFormData({
                  ...formData,
                  fecha: e.target.value
                })} style={inputStyle} data-id="element-1221" data-mp="zcf7uu0l1v7r" />
                  
                  </Field>
                  <Field label="Cliente *" data-id="element-1222" data-mp="ue3tc5zcewj7">
                    <select value={selectedCliente} onChange={e => setSelectedCliente(e.target.value)} style={inputStyle} data-id="element-1223" data-mp="ueww6vv6l1w2">
                    
                      <option value="" data-id="element-1224" data-mp="1n2t12nuwm03x">Selecciona un cliente</option>
                      {clientes.map(c => <option key={c.id} value={c.id} data-id="element-1225" data-mp="1n4vrh8zyxv96">
                          {c.nombre}{c.codigo ? ` (${c.codigo})` : ''}
                        </option>)}
                    </select>
                  </Field>
                  <Field label="Ruta" data-id="element-1226" data-mp="212kbkem7oyd6">
                    <input type="text" value={formData.ruta || ''} onChange={e => setFormData({
                  ...formData,
                  ruta: e.target.value
                })} style={inputStyle} placeholder="Ruta Centro" data-id="element-1227" data-mp="211ix005zltqp" />
                  
                  </Field>
                  <Field label="Cantidad (galones) *" data-id="element-1228" data-mp="zsv71iwh18l">
                    <input type="number" step="0.01" value={formData.cantidad || 0} onChange={e => handleCalculateTotal(parseFloat(e.target.value), formData.precio || 0)} style={inputStyle} placeholder="0" data-id="element-1229" data-mp="y7z37gxta8m" />
                  
                  </Field>
                  <Field label="Precio por galón *" data-id="element-1230" data-mp="oq5aixgh6097">
                    <input type="number" step="0.01" value={formData.precio || 0} onChange={e => handleCalculateTotal(formData.cantidad || 0, parseFloat(e.target.value))} style={inputStyle} placeholder="0.00" data-id="element-1231" data-mp="onsskkapvpw5" />
                  
                  </Field>
                  <Field label="Total (RD$)" data-id="element-1232" data-mp="ap2noihnolx8">
                    <input type="number" value={formData.total || 0} readOnly style={{
                  ...inputStyle,
                  opacity: 0.6,
                  fontWeight: 700,
                  color: T.ok
                }} data-id="element-1233" data-mp="asgovnel2p33" />
                  
                  </Field>
                  <Field label="Supervisor" data-id="element-1234" data-mp="1b05ajveu6rzs">
                    <input type="text" value={formData.supervisor || ''} onChange={e => setFormData({
                  ...formData,
                  supervisor: e.target.value
                })} style={inputStyle} placeholder="Nombre del supervisor" data-id="element-1235" data-mp="1fwvqmiqn5nl7" />
                  
                  </Field>
                  <div style={{
                gridColumn: '1 / -1'
              }} data-id="element-1236" data-mp="1tpbluxdawhbk">
                    <Field label="Observaciones" data-id="element-1237" data-mp="1ynmwvbfc0xvd">
                      <textarea value={formData.observaciones || ''} onChange={e => setFormData({
                    ...formData,
                    observaciones: e.target.value
                  })} style={{
                    ...inputStyle,
                    resize: 'vertical'
                  }} rows={3} placeholder="Notas adicionales..." data-id="element-1238" data-mp="1ymbrx8a5hk2o" />
                    
                    </Field>
                  </div>
                </div>
                <div style={{
              display: 'flex',
              gap: 8,
              marginTop: 16
            }} data-id="element-1239" data-mp="rgia6bglrhnv">
                  <button type="submit" style={primaryButton} data-id="element-1240" data-mp="vhbxp3q4uug8">
                    {editingId ? 'Actualizar' : 'Registrar'} venta
                  </button>
                  <button type="button" onClick={() => {
                setShowForm(false);
                setEditingId(null);
              }} style={ghostButton} data-id="element-1241" data-mp="10jjce12ckom9">
                  
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Tabla de Ventas */}
      <div style={{
      ...glass,
      padding: 20
    }} data-id="element-1242" data-mp="1j8g7g75kjl5j">
        <h3 style={{
        fontSize: 15,
        fontWeight: 800,
        marginBottom: 16,
        color: T.text
      }} data-id="element-1243" data-mp="1o70xxev0qqgs">
          
          Registro de Ventas ({filteredVentas.length})
        </h3>
        {filteredVentas.length === 0 ? <EmptyState icon={<Receipt size={20} data-id="element-1245" data-mp="1y2krhum41d2d" />} title="No hay ventas registradas" hint="Registra una venta nueva o ajusta los filtros de búsqueda." data-id="element-1244" data-mp="1y56z1u903skl" /> : <>
            <div style={{
          overflowX: 'auto'
        }} data-id="element-1246" data-mp="3dnn0a8sp83dd">
              <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 13
          }} data-id="element-1247" data-mp="38otnkil8kge9">
              
                <thead data-id="element-1248" data-mp="33r0g7m875gce">
                  <tr data-id="element-1249" data-mp="33rtgq5gkb9m3">
                    {[{
                  h: 'Factura',
                  a: 'left'
                }, {
                  h: 'Fecha',
                  a: 'left'
                }, {
                  h: 'Cliente',
                  a: 'left'
                }, {
                  h: 'Cantidad (gal)',
                  a: 'right'
                }, {
                  h: 'Precio',
                  a: 'right'
                }, {
                  h: 'Total (RD$)',
                  a: 'right'
                }, {
                  h: 'Ruta',
                  a: 'left'
                }, {
                  h: 'Supervisor',
                  a: 'left'
                }, {
                  h: 'Acciones',
                  a: 'center'
                }].map(({
                  h,
                  a
                }) => <th key={h} style={{
                  textAlign: a as any,
                  padding: '0 10px 10px',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: T.textMuted,
                  borderBottom: `1px solid ${T.border}`
                }} data-id="element-1250" data-mp="23qevqv4qg6lt">
                    
                        {h}
                      </th>)}
                  </tr>
                </thead>
                <tbody data-id="element-1251" data-mp="1ppc9ah9tsuad">
                  {[...filteredVentas].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()).map(venta => <tr key={venta.id} data-id="element-1252" data-mp="3f5v4nra4nu9u">
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  fontFamily: 'monospace',
                  color: T.info,
                  fontWeight: 700
                }} data-id="element-1253" data-mp="3k3woiefdnxmd">
                    
                        {venta.numeroFactura}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  color: T.textSecondary
                }} data-id="element-1254" data-mp="1i7xj9xwy0fna">
                    
                        {venta.fecha}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  fontWeight: 600,
                  color: T.text
                }} data-id="element-1255" data-mp="1m7epwuo3viu1">
                    
                        {venta.clienteNombre}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  textAlign: 'right',
                  color: T.textSecondary
                }} data-id="element-1256" data-mp="6rzfa8gmgjsx">
                    
                        {venta.cantidad.toFixed(2)}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  textAlign: 'right',
                  color: T.textSecondary
                }} data-id="element-1257" data-mp="2u1q49y78xgpg">
                    
                        {venta.precio.toFixed(2)}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  textAlign: 'right',
                  fontWeight: 800,
                  color: T.ok
                }} data-id="element-1258" data-mp="2b8vz1vzfd2yf">
                    
                        {venta.total.toFixed(2)}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  color: T.textSecondary
                }} data-id="element-1259" data-mp="1njkvy7ulqwh4">
                    
                        {venta.ruta || '-'}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  color: T.textSecondary
                }} data-id="element-1260" data-mp="1f3c1k76up4gz">
                    
                        {venta.supervisor || '-'}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  textAlign: 'center'
                }} data-id="element-1261" data-mp="113t72h6yprn5">
                    
                        <div style={{
                    display: 'inline-flex',
                    gap: 6,
                    justifyContent: 'center'
                  }} data-id="element-1262" data-mp="rdhr1wa07rzw">
                      
                          <button onClick={() => handleEdit(venta)} title="Editar" style={{
                      ...iconBtn,
                      color: T.info
                    }} data-id="element-1263" data-mp="ibrzmqg4gt24">
                        
                            <Edit2 size={14} data-id="element-1264" data-mp="3rxp8tftwovh" />
                          </button>
                          <button onClick={() => onDeleteVenta(venta.id)} title="Eliminar" style={{
                      ...iconBtn,
                      color: T.danger
                    }} data-id="element-1265" data-mp="8qininbisyha">
                        
                            <Trash2 size={14} data-id="element-1266" data-mp="3uxz2n8n9nuka" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Resumen */}
            <div style={{
          borderTop: `1px solid ${T.border}`,
          marginTop: 18,
          paddingTop: 18,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 14
        }} data-id="element-1267" data-mp="cphqw3hlsn6m">
            
              <div data-id="element-1268" data-mp="1a5x0e2y1d675">
                <div style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: T.textMuted,
              marginBottom: 4
            }} data-id="element-1269" data-mp="1a66otm3fn5aq">
                
                  Registros
                </div>
                <div style={{
              fontSize: 18,
              fontWeight: 800,
              color: T.info
            }} data-id="element-1270" data-mp="34ktzc5x02x40">
                  {filteredVentas.length}
                </div>
              </div>
              <div data-id="element-1271" data-mp="2bxzqy7dgjo6w">
                <div style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: T.textMuted,
              marginBottom: 4
            }} data-id="element-1272" data-mp="2bzkeve59o8vs">
                
                  Total galones
                </div>
                <div style={{
              fontSize: 18,
              fontWeight: 800,
              color: '#8B5CF6'
            }} data-id="element-1273" data-mp="7nek32gnqpf">
                  {filteredVentas.reduce((sum, v) => sum + v.cantidad, 0).toFixed(2)}
                </div>
              </div>
              <div data-id="element-1274" data-mp="1pqvp8yuuvn5a">
                <div style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: T.textMuted,
              marginBottom: 4
            }} data-id="element-1275" data-mp="1pn7y26cvvctq">
                
                  Monto total
                </div>
                <div style={{
              fontSize: 18,
              fontWeight: 800,
              color: T.ok
            }} data-id="element-1276" data-mp="28g20y1x8fehl">
                  RD$ {filteredVentas.reduce((sum, v) => sum + v.total, 0).toFixed(2)}
                </div>
              </div>
              <div data-id="element-1277" data-mp="2ibn8sgozf8z9">
                <div style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: T.textMuted,
              marginBottom: 4
            }} data-id="element-1278" data-mp="2nct1iein6w8s">
                
                  Precio promedio
                </div>
                <div style={{
              fontSize: 18,
              fontWeight: 800,
              color: T.warn
            }} data-id="element-1279" data-mp="369l34ggajcm8">
                  {(filteredVentas.reduce((sum, v) => sum + v.precio, 0) / filteredVentas.length).toFixed(2)}
                </div>
              </div>
            </div>
          </>}
      </div>
    </div>;
}